import { Modal, Popover, Empty, Tabs as AntTabs, Button, Input as AntInput, ConfigProvider } from "antd";
import { SearchOutlined, TeamOutlined, DeleteOutlined } from "@ant-design/icons";
import moment from "moment";
import React, { useEffect, useRef, useState, useCallback, useContext } from "react";
import { useParams } from "react-router-dom";
import { read, utils } from "xlsx";

// api
import { whatsappBusinessNumbers } from "../../../../../../api/Api";
import { getSingleCampaign } from "../../../../../../api/apiClients/campaign";
import { AudienceSetMembersList, AudienceSetList } from "../../../../../crm/api/Api";

// assets
import { ICONS } from "../../../../../../assets/icons";
import { images } from "../../../../../../assets/images";

// helper
import { cn } from "../../../../../../helper/cn";
import { formatTimeWithAmPm, getTimeStringSeq } from "../../../../../../helper/timeUtils";

// Utility function to convert local date and time to ISO 8601 format with timezone offset
const convertToUTCForBackend = (date, time, period) => {
  if (!date || !time || !period) return null;
  
  // Parse the local time
  const [hours, minutes] = time.split(':').map(Number);
  let hour24 = period === 'PM' ? (hours % 12) + 12 : hours % 12;
  if (hour24 === 12 && period === 'AM') hour24 = 0; // Handle midnight
  
  // Create a moment object in local time
  const localMoment = moment(`${date} ${hour24}:${minutes}`, 'YYYY-MM-DD HH:mm');
  
  // Format as ISO 8601 with timezone offset (e.g., "2025-08-15T00:00:00-04:00")
  return localMoment.format();
};

// Utility function to convert UTC date string from backend to local time
const convertUTCToLocal = (utcDateTime) => {
  if (!utcDateTime || utcDateTime === 'send_now') return { date: '', time: '', displayTime: '' };
  
  // Parse UTC time
  const utcMoment = moment.utc(utcDateTime);
  const localMoment = utcMoment.local();
  
  // Format for display
  const date = localMoment.format('YYYY-MM-DD');
  const time = localMoment.format('HH:mm');
  const displayTime = localMoment.format('hh:mm A');
  
  return { date, time, displayTime };
};

// Validate that scheduled time is at least 60 minutes ahead
const validateMinimumScheduleTime = (date, time, period) => {
  if (!date || !time || !period) return false;
  
  // Parse the local time
  const [hours, minutes] = time.split(':').map(Number);
  let hour24 = period === 'PM' ? (hours % 12) + 12 : hours % 12;
  if (hour24 === 12 && period === 'AM') hour24 = 0; // Handle midnight
  
  // Create a moment object in local time for the scheduled time
  const scheduledMoment = moment(`${date} ${hour24}:${minutes}`, 'YYYY-MM-DD HH:mm');
  
  // Get current time and add 60 minutes
  const minScheduledTime = moment().add(60, 'minutes');
  
  // Check if scheduled time is at least 60 minutes ahead
  return scheduledMoment.isSameOrAfter(minScheduledTime);
};

// redux
import { updateToggleToast } from "../../../../../../reduxToolkit/appSlice";
import {
  useAspDispatch,
  useAspSelector,
} from "../../../../../../test/jest-redux-hooks";

// hooks
import useToggle from "hooks/useToggle";
import { useFileUploader } from "../../../../../../hooks/useFileUploader";

// components
import Xmark from "../../../../../../assets/customSVG/Xmark";
import Calendar from "../../../../../../components/commonComponents/calendar/Calendar";
import Loader from "../../../../../../components/commonComponents/Loader/Index";
import Header from "../../../../../../components/commonComponents/modals/Header";
import TemplateListContainer from "../../../../../../components/commonComponents/templates/TemplateListContainer";
import TemplatePreviewCard from "../../../../../../components/commonComponents/templates/TemplatePreviewCard";
import { Input } from "../../../../../../components/form/Input/Input";
import TimePickers from "../../../../../campaign/components/BroadcastSetting/TimePickers";
import Tabs from "../../Tabs";
import MonthCalender from "../../../../../../components/commonComponents/calendar/MonthCalender";

// providers
import NotificationContext from "../../../../../../providers/NotificationProvider";
import { showSuccessToast, showErrorToast } from "../../../../../../components/commonComponents/gxAntdToast/toastUtils";
import { Base64 } from "js-base64";
import CustomDelete from "../../../../../../assets/customSVG/CustomDelete";
import Group from "../../../../../../assets/customSVG/Group";

const SEND_SETTINGS = [
  {
    id: 1,
    label: "Send Now",
    key: "send_now",
    isDisabled: false,
  },
  {
    id: 2,
    label: "Send Later",
    key: "send_later",
    isDisabled: false,
  },
];

const daily_messaging_limit = {
  TIER_50: "50",
  TIER_250: "250",
  TIER_1K: "1k",
  TIER_10K: "10k",
  TIER_100K: "100k",
  TIER_UNLIMITED: "Unlimited",
};

const STATISTICS_CONFIG = [
  {
    key: 'valid_count',
    icon: ICONS?.BroadcastProfileIcon,
    alt: 'Unique Contacts',
    color: 'blue-500',
    label: 'Unique'
  },
  {
    key: 'duplicate_count',
    icon: ICONS?.BroadcastCopyIcon,
    alt: 'Duplicates',
    color: 'amber-500',
    label: 'Duplicates'
  },
  {
    key: 'invalid_count',
    icon: ICONS?.BroadcastRedInfoIcon,
    alt: 'Invalids',
    color: 'red-500',
    label: 'Invalids'
  }
];

const renderStatistics = (fileData) => {
  return STATISTICS_CONFIG.map(({ key, icon, alt, color, label }) => (
    <div key={key} className="flex items-center gap-1.5">
      <img
        src={icon}
        alt={alt}
        className={`w-3.5 h-3.5 text-${color}`}
      />
      <div className="flex items-center gap-1">
        <span className="text-xs text-gray-500">
          {fileData?.[key] || 0}
        </span>
        <span className="text-xs text-gray-500">{label}</span>
      </div>
    </div>
  ));
};

export default function New({
  data,
  onChange,
  open,
  onFilesSelected,
  sendLatterDate,
  setSendLatterDate,
  allowToEditFlow,
  resetSendLaterDate,
  selectedTemplate,
  setSellectedTemplate,
  sequenceDetails,
}) {
  const { campaign_id } = useParams();
  const { currentBrand, featureFlags } = useAspSelector((state) => state.app);
  const dispatch = useAspDispatch();

  const dropFilesref = useRef();
  const [isLoading, setIsLoading] = useToggle(false);
  const [isLoadings, setIsLoadings] = useToggle(false);
  const [isOpen, setIsOpen] = useToggle(false);
  const [isOpenModal, setIsOpenModal] = useToggle(false);
  const [isDatePopoupOpen, setIsDatePopoupOpen] = useToggle(false);
  const [isTimePopoupOpen, setIsTimePopoupOpen] = useToggle(false);
  const [list, setList] = useState([]);
  const [campaignDetails, setCampaignDetails] = useState(null);
  const [sendSettings, setSendSettings] = useState(SEND_SETTINGS);

  // New state variables for audience set functionality
  const [audienceSets, setAudienceSets] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedAudience, setSelectedAudience] = useState(null);

  // Determine active tab based on data
  const getInitialActiveTab = () => {
    if (data?.data?.audience_set_id) return 'audience-set';
    return 'file-upload';
  };

  const [activeTab, setActiveTab] = useState(getInitialActiveTab());
  const [selectedBroadcastPartType, setSelectedBroadcastPartType] = useState(
    data?.data?.audience_set_id ? 'audience_set' :
    data?.data?.file_path ? 'file_upload' : null
  );

  const [dateTimeError, setDateTimeError] = useState("");

  const { uploading, uploadFiles, handleDropFiles, error } = useFileUploader();
  
  // Context for notifications
  const notification = useContext(NotificationContext);

  const isAudienceManagementSystemEnabled = featureFlags?.ams_feature;

  useEffect(() => {
    if (dropFilesref?.current?.focus()) {
      dropFilesref?.current?.addEventListener("drop", handleDrop);

      return () => {
        dropFilesref?.current?.removeEventListener("drop", handleDrop);
      };
    }
  }, []);

  useEffect(() => {
    const date = moment() < moment(sequenceDetails?.start_date);

    if (date) {
      const res = sendSettings?.map((s) => {
        if (s?.key === "send_now") return { ...s, isDisabled: true };
        return s;
      });
      setSendSettings(res);
    }
  }, [data]);

  useEffect(() => {
    if (currentBrand) {
      getwhatsappBusinessNumbers(currentBrand?.brand_id);
      getCampaignDetails(currentBrand?.brand_id);
    }
  }, [currentBrand]);

  // Fetch audience sets when audience-set tab is active
  const fetchAudienceSets = useCallback(async () => {
    let isMounted = true;

    try {
      setLoading(true);
      const res = await AudienceSetList(currentBrand?.brand_id);

      if (!isMounted) return;

      if (res?.data?.data) {
        setAudienceSets(res.data.data);
      } else {
        setAudienceSets([]);
      }
    } catch (error) {
      console.error('Error fetching audience sets:', error);
      if (isMounted) {
        showErrorToast(notification, "Failed to fetch audience sets");
      }
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }

    return () => { isMounted = false; };
  }, [currentBrand?.brand_id, notification]);

  // Fetch audience sets when the tab is active
  useEffect(() => {
    if (activeTab === 'audience-set') {
      fetchAudienceSets();
    }
  }, [activeTab, fetchAudienceSets]);

  // When data changes, set the correct tab and selected audience type
  useEffect(() => {
    let isMounted = true;

    const initializeData = async () => {
      if (!data?.data) return;

      // Set the active tab and broadcast part type based on existing data
      if (data.data.audience_set_id) {
        setActiveTab('audience-set');
        setSelectedBroadcastPartType('audience_set');
      } else if (data.data.file_path) {
        setActiveTab('file-upload');
        setSelectedBroadcastPartType('file_upload');
      }

      // If we have audience set data but no selected audience, try to find it
      if (data.data.audience_set_id && audienceSets.length > 0) {
        const found = audienceSets.find(a => a.id === data.data.audience_set_id);
        if (found && (!selectedAudience || selectedAudience.id !== found.id)) {
          setSelectedAudience({
            ...found,
            memberCount: data?.data?.broadcast_file_data?.valid_count || 0
          });
        }
      }
    };

    initializeData();

    return () => { isMounted = false; };
  }, [data?.data, audienceSets, selectedAudience]);

  // After audienceSets are fetched, set the selected audience and fetch latest member count if needed
  useEffect(() => {
    let isMounted = true;
    async function setAudienceFromList() {
      if (
        data?.data?.broadcast_part_type === 'audience_set' &&
        data?.data?.audience_set_id &&
        Array.isArray(audienceSets) &&
        audienceSets.length > 0
      ) {
        const found = audienceSets.find(a => a.id === data.data.audience_set_id);
        if (found) {
          // Fetch latest member count
          const memberCount = await fetchAudienceSetMemberCount(found);
          if (isMounted) {
            setSelectedAudience({
              ...found,
              memberCount: memberCount
            });
          }
        }
      }
    }
    setAudienceFromList();
    return () => { isMounted = false; };
  }, [audienceSets, data?.data?.audience_set_id]);

  // Handle initialization of selectedAudience from node data
  useEffect(() => {
    const initializeAudienceData = async () => {
      if (!data?.data?.audience_set_id || audienceSets.length === 0) return;

      const foundAudience = audienceSets.find(a => a.id === data.data.audience_set_id);
      if (!foundAudience) return;

      // Skip if we already have the correct audience with member count
      if (selectedAudience?.id === foundAudience.id && selectedAudience?.memberCount > 0) {
        return;
      }

      // Use the member count from broadcast_file_data if available
      const memberCount = data?.data?.broadcast_file_data?.valid_count;
      
      // If we have a valid member count, use it
      if (memberCount > 0) {
        setSelectedAudience({
          ...foundAudience,
          memberCount: memberCount
        });
      } else {
        // If no member count in data, fetch it
        try {
          const count = await fetchAudienceSetMemberCount(foundAudience);
          // Only update if we got a valid count
          if (count > 0) {
            setSelectedAudience({
              ...foundAudience,
              memberCount: count
            });
            
            // Also update the node data with the fetched count
            onChange({
              type: "UPDATE_NODE",
              id: data?.uniq_id,
              key: "data",
              data: {
                ...data?.data,
                broadcast_part_type: 'audience_set',
                broadcast_file_data: {
                  valid_count: count,
                  duplicate_count: 0,
                  invalid_count: 0
                },
                settings: {
                  ...data?.data?.settings,
                  contacts_count: count
                }
              }
            });
          }
        } catch (error) {
          console.error('Error initializing audience data:', error);
        }
      }
    };

    initializeAudienceData();
  }, [data?.data?.audience_set_id, data?.data?.broadcast_file_data?.valid_count, audienceSets, data?.data, data?.uniq_id, onChange]);

  const getwhatsappBusinessNumbers = async (brand_id) => {
    setIsLoading(true);
    setIsLoadings(true);
    try {
      const res = await whatsappBusinessNumbers(brand_id);
      setList(res?.data?.wapp_business_num);
      count = res?.data?.wapp_business_num_count;
    } catch (error) {
      console.warn(error);
    } finally {
      setIsLoading(false);
      setIsLoadings(false);
    }
  };

  const getCampaignDetails = async (brand_id) => {
    setIsLoading(true);
    try {
      const res = await getSingleCampaign(brand_id, campaign_id);
      setCampaignDetails(res?.data);
    } catch (error) {
      console.warn(error);
    } finally {
      setIsLoading(false);
    }
  };

  const isOptionSelected = (option) => {
    return option?.id === data?.sendFrom?.id;
  };

  const handleFileChange = async (event) => {
    // Don't allow file upload if an audience set is selected
    if (selectedBroadcastPartType && selectedBroadcastPartType === 'audience_set') {
      showErrorToast(notification, 'Please remove the selected audience set before uploading a file');
      return;
    }

    const selectedFiles = event.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      const filesData = await handleFile(selectedFiles?.[0]);

      if (filesData) {
        const uploadFileRes = await uploadFiles(event, "DOCUMENT");

        const { settings, broadcast_file_data } = filesData;

        // Set the selected broadcast part type
        setSelectedBroadcastPartType('file_upload');

        onChange({
          type: "UPDATE_NODE",
          id: data?.uniq_id,
          key: "data",
          data: {
            ...data?.data,
            broadcast_file_data,
            file_path: uploadFileRes,
            file_name: event.target.files[0]?.name,
            broadcast_part_type: 'file_upload',
            settings: { ...data?.data?.settings, ...settings },
          },
        });
      }
    }
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    
    // Don't allow file upload if an audience set is selected
    if (selectedBroadcastPartType && selectedBroadcastPartType === 'audience_set') {
      showErrorToast(notification, 'Please remove the selected audience set before uploading a file');
      return;
    }
    
    const droppedFiles = event.dataTransfer.files;
    if (droppedFiles.length > 0) {
      const newFiles = Array.from(droppedFiles);
      const uploadFileRes = await handleDropFiles(newFiles, [".xlsx"]);
      const filesData = await handleFile(droppedFiles[0]);

      if (filesData) {
        const uploadFileRes = await uploadFiles(event, "DOCUMENT");

        const { settings, broadcast_file_data } = filesData;

        // Set the selected broadcast part type
        setSelectedBroadcastPartType('file_upload');

        onChange({
          type: "UPDATE_NODE",
          id: data?.uniq_id,
          key: "data",
          data: {
            ...data?.data,
            broadcast_file_data,
            file_path: uploadFileRes,
            file_name: event.target.files[0]?.name,
            broadcast_part_type: 'file_upload',
            settings: { ...data?.data?.settings, ...settings },
          },
        });
      }
    }
  };

  const handleClearFiles = (event) => {
    event.stopPropagation();
    event.preventDefault();

    const newData = { ...data?.data };
    delete newData["broadcast_file_data"];
    delete newData["file_path"];
    delete newData["file_name"];

    // Clear the selected broadcast part type
    setSelectedBroadcastPartType(null);

    onChange({
      type: "UPDATE_NODE",
      id: data?.uniq_id,
      key: "data",
      data: newData,
    });
  };

  const handleCanceltemplateModal = () => setIsOpenModal(false);

  const handleFile = async (file) => {
    const phoneRegex =
      /^(91 [0-9]{10}|65 [0-9]{8}|44 [0-9]{10}|971 [0-9]{9}|1 [0-9]{10}|353 [0-9]{9})$/;

    try {
      const fileData = await file.arrayBuffer();
      const workbook = read(fileData, { type: "array" });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      const contacts = utils.sheet_to_json(worksheet);

      const {
        validData,
        invalidData,
        duplicateData,
        validContacts,
        duplicateContacts,
        invalidContacts,
      } = processContacts(contacts, phoneRegex);

      let segmentSettings = {};

      if (data?.data?.settings?.send_setting === "send_later")
        segmentSettings = { schedule_on: data?.data?.settings?.send_later_dt };
      else segmentSettings = { delay: 0 };

      const datas = {
        broadcast_file_data: {
          valid_count: validContacts.length,
          duplicate_count: duplicateContacts.length,
          invalid_count: invalidContacts.length,
          file_date_valid: validData,
          file_date_in_valid: invalidData,
          file_date_duplicate: duplicateData,
        },
        settings: {
          segment_settings: [
            {
              segments: [
                {
                  id: 1,
                  end: validContacts.length,
                  flag: true,
                  start: 0,
                  ...segmentSettings,
                },
              ],
            },
          ],
          contacts_count: validContacts.length,
          // send_from_phone_number: '555-015-6358',
          // send_from_phone_number_id: '251617158032885',
        },
      };

      return datas;
    } catch (error) {
      console.error("Error uploading file:", error);
    }
  };

  const processContacts = (contacts, phoneRegex) => {
    const uniquePhoneNumbers = new Set();
    const validContacts = [];
    const duplicateContacts = [];
    const invalidContacts = [];

    const brandPlanLimits = {
      "magnum": 100000,
      "growth": 50000,
      "starter": 25000,
    };

    const limit =
      brandPlanLimits[currentBrand?.brand_billing_plan_name] || contacts.length;
    const contactsToValidate = contacts.slice(0, limit);

    contactsToValidate.forEach((contact) => {
      const phoneNumber = contact["mobile-number"]
        ? String(contact["mobile-number"])?.replace(/\s+/g, "")
        : null;

      // Validate only if there is a mobile number
      if (phoneNumber) {
        const countryCode = contact["country-code"]
          ? String(contact["country-code"]).trim()
          : "";

        if (!countryCode) {
          // Mark as invalid if either phone number or country code is missing
          invalidContacts.push({
            ...contact,
            reason: !countryCode
              ? "Missing country code"
              : "Missing phone number",
          });
          return;
        }

        const fullPhoneNumber = `${countryCode} ${phoneNumber}`;

        const isValid = phoneRegex.test(fullPhoneNumber);
        const isDuplicate = uniquePhoneNumbers.has(fullPhoneNumber);

        if (isValid && !isDuplicate) {
          uniquePhoneNumbers.add(fullPhoneNumber);
          validContacts.push(contact);
        } else if (isValid && isDuplicate) {
          duplicateContacts.push(contact);
        } else {
          invalidContacts.push(fullPhoneNumber);
        }
      }
    });

    const mapContactData = (contactList) =>
      contactList.map((contact) => {
        const getFieldValue = (field) => {
          let value = contact[field];

          if (value !== undefined && value !== null && value !== "") {
            value = String(value).trim();

            if (field === "first-name" || field === "last-name") {
              value = value.replace(/[^a-zA-Z\s]/g, "");
            }

            return value || null;
          }

          return null;
        };

        const details = {
          "email-id": getFieldValue("email-id"),
          "last-name": getFieldValue("last-name"),
          "media-url": getFieldValue("media-url"),
          "shop-name": getFieldValue("shop-name"),
          "first-name": getFieldValue("first-name"),
          "website-url": getFieldValue("website-url"),
          "mobile-number": getFieldValue("mobile-number"),
          "custom-variable1": getFieldValue("custom-variable1"),
          "custom-variable2": getFieldValue("custom-variable2"),
          "custom-variable3": getFieldValue("custom-variable3"),
          "custom-variable4": getFieldValue("custom-variable4"),
          "custom-variable5": getFieldValue("custom-variable5"),
          "otp-number": getFieldValue("otp-number"),
        };
        // Filter out keys with null values
        const filteredDetails = Object.fromEntries(
          Object.entries(details).filter(([_, value]) => value !== null)
        );

        return {
          row: contact.__rowNum__ || null,
          mobile1: getFieldValue("country-code")
            ? getFieldValue("country-code") +
              " " +
              getFieldValue("mobile-number")?.replace(/\s+/g, "")
            : getFieldValue("mobile-number")?.replace(/\s+/g, "") || null,
          details: filteredDetails,
        };
      });

    if (validContacts.length === 0 && contacts[0]["mobile-number"]) {
      // setFileLoader(false);
      dispatch(
        updateToggleToast([
          ...toggleToast,
          {
            id: toggleToast.length + 1,
            content:
              "No contacts found. Add at least one contact to send a broadcast.",
            status: "Error",
            duration: "",
          },
        ])
      );
      return;
    }

    const validData =
      validContacts.length > 0 ? mapContactData(validContacts) : 0;
    const invalidData =
      invalidContacts.length > 0 ? mapContactData(invalidContacts) : 0;
    const duplicateData =
      duplicateContacts.length > 0 ? mapContactData(duplicateContacts) : 0;

    return {
      validData,
      invalidData,
      duplicateData,
      validContacts,
      duplicateContacts,
      invalidContacts,
    };
  };

  // Function to fetch member count for an audience set
  const fetchAudienceSetMemberCount = async (audience) => {
    try {
      if (!audience) return 0;

      const { selection_criteria, added_contact_person_ids = [], removed_contact_person_ids = [] } = audience.details || {};
      
      // Encode the selection criteria
      const filters = selection_criteria ? Base64.encode(JSON.stringify(selection_criteria)) : '';

      // Fetch just the first page with limit=1 to get the total count
      const response = await AudienceSetMembersList(
        currentBrand?.brand_id,
        `filters=${filters}`,
        added_contact_person_ids,
        removed_contact_person_ids,
        1, // page
        1  // limit=1 since we only need the count
      );
      
      const count = response?.data?.data?.pagination?.total_count || 0;
      return count;
    } catch (error) {
      console.error('Error in fetchAudienceSetMemberCount:', error);
      return 0;
    }
  };

  const handleAudienceSelect = async (audience) => {
    try {
      // Don't allow selecting an audience if a file is already uploaded
      if (selectedBroadcastPartType && selectedBroadcastPartType === 'file_upload') {
        showErrorToast(notification, "Please remove the existing file upload before selecting an audience set");
        return;
      }

      setLoading(true);
      
      // Fetch the member count for the selected audience
      const memberCount = await fetchAudienceSetMemberCount(audience);
      
      // Create segment settings based on send setting
      let segmentSettings = {};
      if (data?.data?.settings?.send_setting === "send_later") {
        segmentSettings = { schedule_on: data?.data?.settings?.send_later_dt };
      } else {
        segmentSettings = { delay: 0 };
      }

      // Prepare the updated data with proper structure
      const updatedData = {
        ...data?.data,
        // Clear any file-related data
        file_path: null,
        file_name: null,
        // Set audience set data
        audience_set_id: audience.id,
        audience_set_name: audience.name,
        audience_set_created_at: audience.created_at,
        broadcast_part_type: 'audience_set',
        // Set file data structure with member count
        broadcast_file_data: {
          valid_count: memberCount,
          duplicate_count: 0,
          invalid_count: 0,
        },
        // Update settings
        settings: {
          ...data?.data?.settings,
          segment_settings: [
            {
              segments: [
                {
                  id: 1,
                  end: memberCount,
                  flag: true,
                  start: 0,
                  ...segmentSettings,
                },
              ],
            },
          ],
          contacts_count: memberCount,
        },
      };

      onChange({
        type: "UPDATE_NODE",
        id: data?.uniq_id,
        key: "data",
        data: updatedData
      });
      
      // Update local state
      setSelectedAudience({
        ...audience,
        memberCount: memberCount
      });
      setSelectedBroadcastPartType('audience_set');

      showSuccessToast(notification, 'Audience set selected successfully');
    } catch (error) {
      console.error('Error selecting audience set:', error);
      showErrorToast(notification, "Failed to select audience set. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAudience = async () => {
    setLoading(true);
    
    try {
      // Clear the selected audience
      setSelectedAudience(null);
      
      // Reset the selected broadcast part type
      setSelectedBroadcastPartType(null);
      
      // Clear audience set data from the node
      const newData = { ...data?.data };
      delete newData["audience_set_id"];
      delete newData["audience_set_name"];
      delete newData["audience_set_created_at"];
      delete newData["broadcast_file_data"];

      onChange({
        type: "UPDATE_NODE",
        id: data?.uniq_id,
        key: "data",
        data: newData,
      });

      showSuccessToast(notification, 'Audience set removed successfully');
    } catch (error) {
      console.error('Error deleting audience set:', error);
      showErrorToast(notification, "Failed to remove audience set. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const filteredAudienceSets = Array.isArray(audienceSets) 
    ? audienceSets.filter(audience => 
        audience?.name?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  const formatFileSize = (audience) => {
    const count = audience?.memberCount || audience?.valid_count || 0;
    return `${count} contact${count !== 1 ? 's' : ''}`;
  };

  const renderFileUploadTab = () => (
    <div
      ref={dropFilesref}
      className="flex flex-col gap-3"
      onDrop={handleDrop}
      onDragOver={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
    >
        {uploading ? (
          <div className="w-full h-32 bg-[var(--BG-50)] flex flex-col items-center justify-center border border-dashed rounded-2xl cursor-pointer">
            <Loader Width={25} Height={25} />
          </div>
        ) : data?.data?.file_path ? (
          <div className="w-full bg-gray-100 rounded-lg px-5 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <img
                  src={ICONS?.AudienceFileIcon}
                  alt="csv icon"
                  className="size-5"
                />
                <div>
                  <h6 className="text-xs weight-medium text-[var(--contentBlack)]">
                    {data?.data?.file_name}
                  </h6>
                </div>
              </div>
              <button
                className="size-6 flex items-center justify-center rounded-full bg-gray-200 transition-all ease-linear duration-100 group/clearFiles hover:bg-red-200"
                onClick={handleClearFiles}
              >
                <Xmark
                  width={9}
                  height={9}
                  color=""
                  className="stroke-gray-600 group-hover/clearFiles:stroke-red-500"
                />
              </button>
            </div>
            {data?.data?.broadcast_file_data && (
              <div className="mt-3 pt-3 border-gray-200">
                <div className="flex items-center gap-6">
                  {renderStatistics(data.data.broadcast_file_data)}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="">
            <input
              type="file"
              hidden
              id="browse"
              onChange={handleFileChange}
              accept=".xlsx"
            />
            <label
              htmlFor="browse"
              className={cn(
                "w-full h-32 bg-[var(--BG-50)] flex flex-col items-center justify-center gap-1 border border-dashed rounded-2xl cursor-pointer",
                error ? "border-[var(--fontRed)]" : "border-[var(--border-100)]"
              )}
            >
              <img src={images?.File} alt="upload image" />
              <p className="text-center">
                Click or Drag & Drop the file to upload <br /> .xlsv
              </p>
            </label>
          </div>
        )}
        {error && (
          <p className="text-[var(--fontRed)] text-sm weight-medium">
            Unsupported format
          </p>
        )}
    </div>
  );

  const renderAudienceSetTab = () => (
    <div className="audience-set-tab">
      <div className="search-container mb-6">
        <AntInput
          placeholder="Search audience sets"
          prefix={<SearchOutlined />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ width: '100%', maxWidth: '400px' }}
        />
      </div>
      
      <div className="upload-container" style={{ minHeight: '200px', maxHeight: '300px', overflowY: 'auto', padding: '16px' }}>
        {loading ? (
          <div className="flex-row justify-center align-center" style={{ height: '100%' }}>
            <Loader Width={24} Height={24} />
          </div>
        ) : filteredAudienceSets.length > 0 ? (
          <div className="audience-list">
            {filteredAudienceSets.map((audience) => (
              <div 
                key={audience.id}
                className={`audience-item ${selectedAudience?.id === audience.id ? 'selected' : ''}`}
                onClick={() => handleAudienceSelect(audience)}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 16px',
                  marginBottom: '8px',
                  backgroundColor: selectedAudience?.id === audience.id ? '#f0f7ff' : '#fff',
                  border: `1px solid ${selectedAudience?.id === audience.id ? '#1890ff' : '#f0f0f0'}`,
                  borderRadius: '4px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <div>
                  <div style={{ fontWeight: 500, marginBottom: '4px' }}>{audience.name}</div>
                </div>
                <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                  {new Date(audience.created_at).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex-row justify-center align-center" style={{ height: '100%' }}>
            <Empty 
              description={
                <span style={{ color: '#8c8c8c' }}>
                  {searchQuery 
                    ? 'No audience sets found matching your search' 
                    : 'No audience sets found. Please create an audience set first.'
                  }
                </span>
              }
              style={{ margin: '40px 0' }}
            />
          </div>
        )}
      </div>

      {(selectedAudience || (data?.data?.audience_set_id && data?.data?.audience_set_name)) && (
        <div className="mt-4 bg-white border border-gray-200 rounded-lg p-3 flex items-center justify-between">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '4px',
              backgroundColor: '#f0f7ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Group width="20px" height="20px" />
            </div>
            <div>
              <div style={{ 
                fontSize: '14px',
                color: '#262626',
                marginBottom: '4px',
                fontWeight: 500
              }}>
                {selectedAudience?.name || data?.data?.audience_set_name}
              </div>
              <div style={{ 
                fontSize: '12px',
                color: '#8c8c8c',
                display: 'flex',
                gap: '12px'
              }}>
                <span>{formatFileSize(selectedAudience) || data?.data?.broadcast_file_data?.valid_count || `0 contacts`}</span>
                <span>•</span>
                <span>Uploaded on {selectedAudience?.created_at ? new Date(selectedAudience.created_at).toLocaleDateString() :
                  (data?.data?.audience_set_created_at ? new Date(data.data.audience_set_created_at).toLocaleDateString() : 'N/A')}</span>
              </div>
            </div>
          </div>
          <Button 
            type="text" 
            icon={loading ? (
              <Loader Width={20} Height={20} loaderBg="white" />
            ) : (
                <CustomDelete color="#F96056" width="1vw" height="1vw" />
            )}
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteAudience();
            }}
            disabled={loading}
          />
        </div>
      )}
    </div>
  );

  const setHours = (value) => {
    setSendLatterDate((prev) => {
      const timeString = updateTime({
        date: prev?.selectedDate,
        hours: value,
        minutes: prev?.minutes,
        periods: prev?.periods,
      });
      return { ...prev, hours: value, selectedTime: timeString };
    });
  };

  const setMinutes = (value) => {
    setSendLatterDate((prev) => {
      const timeString = updateTime({
        date: prev?.selectedDate,
        hours: prev?.hours,
        minutes: value,
        periods: prev?.periods,
      });
      return { ...prev, minutes: value, selectedTime: timeString };
    });
  };

  const setPeriod = (value) => {
    setSendLatterDate((prev) => {
      const timeString = updateTime({
        date: prev?.selectedDate,
        hours: prev?.hours,
        minutes: prev?.minutes,
        periods: value,
        updateKey: "periods",
      });
      return { ...prev, periods: value, selectedTime: timeString };
    });
  };

  const handleDateSelection = (date) => {
    const formattedDate = moment(date).format("YYYY-MM-DD");

    const startDate = campaignDetails?.starting_date_and_time;
    const endDate = campaignDetails?.ending_date_and_time;
    const datePart = moment(startDate?.split("T")[0], "YYYY-MM-DD");
    const endDatePart = moment(endDate?.split("T")[0], "YYYY-MM-DD");

    if (moment(formattedDate).isBefore(datePart, "day")) return;
    if (moment(formattedDate).isAfter(endDatePart, "day")) return;

    // Update the date in local time
    setSendLatterDate((prev) => ({
      ...prev,
      selectedDate: formattedDate,
      // Keep the existing time components
      hours: prev?.hours || 12, // Default to 12 if not set
      minutes: prev?.minutes || 0, // Default to 00 if not set
      periods: prev?.periods || 'AM', // Default to AM if not set
    }));

    // Trigger time update to refresh the display and update the backend
    const timeString = updateTime({
      date: formattedDate,
      hours: sendLatterDate?.hours || 12,
      minutes: sendLatterDate?.minutes || 0,
      periods: sendLatterDate?.periods || 'AM',
    });

    setSendLatterDate(prev => ({
      ...prev,
      selectedTime: timeString,
      selectedDate: formattedDate
    }));

    setIsDatePopoupOpen(false);
  };

  const updateTime = ({ date, hours, minutes, periods, updateKey = null }) => {
    const timeString = getTimeStringSeq(hours, minutes, periods, updateKey);
    
    // Convert local time to UTC for backend
    const utcDateTime = convertToUTCForBackend(date, `${hours}:${minutes}`, periods);
    
    // Format the local time for display
    const localDateTime = `${date} ${timeString}`;

    let segmentSettings = data?.data?.settings?.segment_settings;

    if (
      ((data?.data?.file_path &&
      data?.data?.file_name) ||
      (data?.data?.audience_set_id &&
      data?.data?.audience_set_name)) &&
      data?.data?.settings?.send_setting === "send_later"
    ) {
      // Store the UTC time in the schedule_on field for the backend
      segmentSettings = [
        {
          segments: [
            {
              ...segmentSettings?.[0]?.["segments"]?.[0],
              schedule_on: utcDateTime || localDateTime,
            },
          ],
        },
      ];
    }

    // Update local state with the display time
    setSendLatterDate(prev => ({
      ...prev,
      selectedDate: date,
      selectedTime: timeString,
      hours,
      minutes,
      periods
    }));

    // Send the UTC time to the backend
    onChange({
      type: "UPDATE_NODE",
      id: data?.uniq_id,
      primaryKey: "data",
      key: "settings",
      data: {
        ...data?.data?.settings,
        send_later_dt:
          data?.data?.settings?.send_setting === "send_later"
            ? utcDateTime // Always use the UTC formatted date
            : "",
        segment_settings: segmentSettings,
      },
    });

    return timeString;
  };

  const handleTimeClick = () => {
    setIsTimePopoupOpen(false);
    setIsDatePopoupOpen(false);

    // Validate we have all required time components
    if (!sendLatterDate?.hours || !sendLatterDate?.minutes || !sendLatterDate?.periods || !sendLatterDate?.selectedDate) {
      setDateTimeError("Please select a valid date and time");
      return;
    }

    // Format the selected time for display
    const newSelectedTime = getTimeStringSeq(
      sendLatterDate.hours,
      sendLatterDate.minutes,
      sendLatterDate.periods
    );

    // Validate minimum schedule time (60 minutes ahead)
    const isValidTime = validateMinimumScheduleTime(
      sendLatterDate.selectedDate,
      `${sendLatterDate.hours}:${sendLatterDate.minutes}`,
      sendLatterDate.periods
    );

    if (!isValidTime) {
      setDateTimeError("Scheduled time should be at least 60 minutes ahead of the current time");
      return;
    }

    // Clear any previous errors
    setDateTimeError("");

    // Convert to UTC for backend
    const utcDateTime = convertToUTCForBackend(
      sendLatterDate.selectedDate,
      `${sendLatterDate.hours}:${sendLatterDate.minutes}`,
      sendLatterDate.periods
    );

    if (!utcDateTime) {
      setDateTimeError("Invalid date/time format");
      return;
    }

    // Update the time in local format for display
    updateTime({
      date: sendLatterDate.selectedDate,
      hours: sendLatterDate.hours,
      minutes: sendLatterDate.minutes,
      periods: sendLatterDate.periods,
      updateKey: "update",
    });
  };

  return (
    <div>
      <div className="flex flex-col gap-3 py-5 border-b border-[var(--border-100)] ">
        <div className="flex flex-col gap-0.5">
          <h5>Send From</h5>
          <p>Select a WhatsApp number you would like to send messages.</p>
        </div>
        <Popover
          open={isOpen}
          placement="bottomLeft"
          arrow={false}
          content={
            <ul
              className={`w-full min-w-[27rem] m-0 list-none rounded-2xl flex-col gap-1.5 overflow-y-scroll listScroll bg-[var(--white)] z-50 p-2.5 ${
                isOpen ? "flex" : "hidden"
              }`}
            >
              {isLoadings ? (
                <div className="w-100 h-[7vh] flex-row align-center justify-center">
                  <Loader Width={25} Height={25} />
                </div>
              ) : (
                <>
                  {list?.map((option) => {
                    return (
                      <li
                        key={option?.id}
                        className="w-full h-[80px] flex items-center justify-between px-2.5 py-1.5 border-b border-[var(--border-100)] last:border-0 cursor-pointer"
                        onClick={() => {
                          setIsOpen(false);
                          onChange({
                            type: "UPDATE_NODE",
                            id: data?.uniq_id,
                            primaryKey: "data",
                            key: "settings",
                            data: {
                              ...data?.data?.settings,
                              channel: "whatsapp",
                              send_from_phone_name:
                                option?.send_from_phone_name,
                              send_from_phone_number:
                                option?.send_from_phone_number,
                              send_from_phone_number_id:
                                option?.send_from_phone_number_id,
                              send_from_country_code: option?.country_code,
                            },
                          });
                        }}
                      >
                        <div className="flex flex-col gap-2.5">
                          <div className="flex items-center justify-between gap-5">
                            <p>{option?.send_from_phone_name}</p>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5 text-sm weight-medium text-[#616874] pr-3.5">
                              {option?.country_code}
                              {option?.send_from_phone_number}
                            </div>
                            <div className="flex items-center gap-1.5 text-xs weight-semibold text-[var(--contentText)]  border-x border-[var(--border-100)] px-3.5">
                              <p className="size-2.5 bg-[var(--primary)] rounded-sm"></p>
                              <p>High</p>
                            </div>
                            <div className="flex items-center gap-1.5 text-xs weight-semibold text-[var(--contentText)] px-3.5">
                              {daily_messaging_limit[option?.daily_messaging_limit]}/24hr
                            </div>
                          </div>
                        </div>
                        {isOptionSelected(option) && (
                          <img
                            src={ICONS?.Selected}
                            alt="selected"
                            className="size-5"
                          />
                        )}
                      </li>
                    );
                  })}
                </>
              )}
            </ul>
          }
        >
          <div
            className="w-full h-12 flex items-center justify-between border boder-[var(--border-50)] px-5 py-1 rounded-lg cursor-pointer"
            onClick={() => {
              setIsOpen(!isOpen);
            }}
          >
            {data?.data?.settings?.send_from_phone_name ? (
              <div
                className={`flex-grow flex items-center gap-2.5 text-[var(--contentText)]`}
              >
                <p>{data?.data?.settings?.send_from_phone_name}</p>
                <p>
                  {data?.data?.settings?.send_from_country_code} 
                  {data?.data?.settings?.send_from_phone_number}
                </p>
              </div>
            ) : (
              <div className="w-full flex items-center font-md weight-medium text-[var(--font-300)]">
                Please select
              </div>
            )}

            {/* <div
              className={`transition-transform duration-200 ease-linear cursor-pointer ${
                isOpen ? '-rotate-180' : 'rotate-0'
              }`}>
              <div
                className={`border rounded-t-1.5 border-transparent border-t-[#777] translate-y-[25%]`}></div>
            </div> */}
            <img
              src={ICONS?.modelDropdownIcon}
              className={cn(
                "transition-transform duration-200 ease-linear cursor-pointer",
                isOpen ? "-rotate-180" : "rotate-0"
              )}
            />
          </div>
        </Popover>
      </div>

      {/* Upload Audience Section */}
      <div className="flex flex-col gap-3 py-5 border-b border-[var(--border-100)] ">
        <div className="flex flex-col gap-0.5">
          <h5>Upload Audience</h5>
          <p>
            Upload a audience list or select an audience set to tailor your message to specific groups.
          </p>
        </div>
        <ConfigProvider 
          theme={{
            components: {
              Tabs: {
                inkBarColor: "#05805d", // active bar color
                colorPrimary: "#05805d",
              },
            },
          }}
        >
          <AntTabs
            activeKey={activeTab}
            onChange={(key) => {
              // Don't allow changing tabs if one is already selected
              if (data?.data?.file_path || data?.data?.audience_set_id) return;
              setActiveTab(key);
            }}
            items={[
              {
                key: 'file-upload',
                label: 'File Upload',
                children: renderFileUploadTab(),
                disabled: data?.data?.audience_set_id // Disable if audience set is selected
              },
              ...(isAudienceManagementSystemEnabled ? [
                {
                  key: 'audience-set',
                  label: 'Audience Set',
                  children: renderAudienceSetTab(),
                  disabled: !!data?.data?.file_path // Disable if file is uploaded
                }
              ] : []),
            ]}
          />
        </ConfigProvider>
      </div>

      {/* Template Section */}
      <div className="flex flex-col gap-3 py-5 border-b border-[var(--border-100)] ">
        <div className="flex flex-col gap-0.5">
          <h5>Select Template</h5>
          <p>Select the approved templates to broadcast your audience.</p>
        </div>
        {selectedTemplate ? (
          <TemplatePreviewCard
            category={selectedTemplate?.category}
            categoryType={selectedTemplate?.subcategory_type}
            name={selectedTemplate?.name}
            language={selectedTemplate?.language}
            onChange={() => {
              if (allowToEditFlow) setIsOpenModal(true);
            }}
          />
        ) : (
          <div
            className="w-full h-auto py-1 flex items-center justify-center border border-[]"
            onClick={() => {
              setIsOpenModal(true);
            }}
          >
            Choose template
          </div>
        )}
      </div>

      {/* Send Settings Section */}
      <div className="flex flex-col gap-3 py-5 border-b border-[var(--border-100)] ">
        <div className="flex flex-col gap-0.5">
          <h5>Send Settings</h5>
          <p>When should we send the broadcast?</p>
        </div>
        <Tabs
          tabs={sendSettings}
          selected={{ key: data?.data?.settings?.send_setting || "send_now" }}
          onTabChange={(value) => {
            if (
              data?.data?.settings?.send_setting === value?.key ||
              value?.isDisabled
            )
              return;

            let datas = {
              ...data?.data?.settings,
              send_setting: value?.key,
              send_later_dt:
                value?.key === "send_now"
                  ? "send_now"
                  : data?.data?.settings?.send_later_dt ?? "",
            };

            if (value?.key === "send_later") {
              const segment = {
                ...datas["segment_settings"]?.[0]?.segments?.[0],
              };
              delete segment?.delay;
              datas["segment_settings"] = [{ segments: [segment] }];
            } else {
              resetSendLaterDate();

              const segment = {
                ...datas["segment_settings"]?.[0]?.segments?.[0],
                delay: 0,
              };
              delete segment?.schedule_on;
              datas["segment_settings"] = [{ segments: [segment] }];
            }

            onChange({
              type: "UPDATE_NODE",
              id: data?.uniq_id,
              primaryKey: "data",
              key: "settings",
              data: datas,
            });
          }}
        />
        {data?.data?.settings?.send_setting === "send_later" && (
          <div>
            <div className="flex flex-col md:flex-row md:items-center md:space-x-5 w-full gap-2">
            {/* Date Picker */}
            <div className="flex flex-col w-full md:w-auto">
              <label className="text-xs mb-1">Date</label>
              <MonthCalender
                allowedDate="allowed"
                dateRange={{
                  startDate: sequenceDetails?.start_date,
                  endDate: sequenceDetails?.end_date,
                }}
                onUpdate={handleDateSelection}
              >
                <div className="w-full">
                  <Input
                    type="none"
                    placeholder="DD/MM/YY"
                    iconPrefix={ICONS?.BroadcastCalendarIcon}
                    className="w-full md:w-[7vw] text-xs py-2 px-3 text-black cursor-pointer"
                    value={sendLatterDate?.selectedDate}
                    readOnly
                    name="calender"
                  />
                </div>
              </MonthCalender>
            </div>

            {/* Time Picker */}
            <div className="flex flex-col w-full md:w-auto mt-4 md:mt-0">
              <label className="text-xs mb-1">Time</label>
              <Popover
                overlayClassName="time-popover"
                content={
                  <TimePickers
                    hours={sendLatterDate?.hours}
                    minutes={sendLatterDate?.minutes}
                    periods={sendLatterDate?.periods}
                    setHours={setHours}
                    setMinutes={setMinutes}
                    setPeriod={setPeriod}
                    onOkClick={() => handleTimeClick(isTimePopoupOpen)}
                  />
                }
                trigger="click"
                arrow={false}
                open={isTimePopoupOpen}
                onOpenChange={() => setIsTimePopoupOpen()}
              >
                <Input
                  placeholder="00:00 AM"
                  iconPrefix={ICONS?.BroadcastTimerIcon}
                  className="w-full md:w-[7.5vw] text-xs py-2 px-3 cursor-pointer"
                  value={`${sendLatterDate?.selectedTime}` || ""}
                />
              </Popover>
            </div>
            </div>
            {error && (
              <div className="error-message flex-row align-center pl-10 ml-10 gap-2">
                <img src={ICONS?.alertIconError} alt="alertIconError" />
                {error}
              </div>
            )}
          </div>
        )}
      </div>
      <Modal
        open={isOpenModal}
        centered
        footer={null}
        styles={{content: {padding: 0}}}
        closable={false}
        mask={true}
        wrapClassName={"bg-[#00000095]"}
        onCancel={handleCanceltemplateModal}
        destroyOnClose
        width={"60vw"}
        style={{
          width: "100%",
          borderRadius: 20,
          boxShadow: "0px 10px 24px 0px rgba(79, 92, 128, 0.15)",
        }}
      >
        <Header
          title="Choose template"
          titleSize="largest"
          rootStyle="!px-[1.5vw] !py-[1vw]"
          handleCancel={handleCanceltemplateModal}
        />

        <TemplateListContainer
          className="h-[58vh]"
          changeMedia={false}
          changeVariable={false}
          sendTemplate={(templateData) => {
            setSellectedTemplate(templateData);

            onChange({
              type: "UPDATE_NODE",
              id: data?.uniq_id,
              primaryKey: "data",
              key: "settings",
              data: {
                ...data?.data?.settings,
                template_id: templateData?.message_template_id,
                template_name: templateData?.name,
                template_subcategory_type: templateData?.subcategory_type,
                template_category: templateData?.category,
              },
            });

            handleCanceltemplateModal();
          }}
        />
      </Modal>
    </div>
  );
}
